import React, { useState, useEffect } from 'react';
import { db, auth } from "../firebase/fire";
import { collection, getDocs, doc, getDoc, updateDoc, addDoc, arrayUnion, query, where } from "firebase/firestore";
import { signInWithEmailAndPassword } from "firebase/auth";
import moment from 'moment';
import Calendar from './Calendar';
import { useNavigate } from "react-router-dom";
import Modal from './Modal';
import { FaHome, FaChevronRight, FaCalendarAlt, FaSignInAlt, FaClock, FaUsers, FaEnvelope, FaLock, FaCheckCircle } from 'react-icons/fa';

const ClassBooking = () => {
  const [classSchedule, setClassSchedule] = useState([]);
  const [selectedClass, setSelectedClass] = useState(null);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentStep, setCurrentStep] = useState('calendar'); 
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [hoursBeforeBookingClose, setHoursBeforeBookingClose] = useState(1); 

  const showModal = (message) => {
    setModalMessage(message);
    setModalOpen(true);
  };

  useEffect(() => {
    fetchClassSchedule();
    fetchBookingSettings();
  }, []);

  const fetchBookingSettings = async () => {
    try {
      const bookingDoc = await getDoc(doc(db, "settings", "booking"));
      if (bookingDoc.exists()) {
        setHoursBeforeBookingClose(bookingDoc.data().hoursBeforeBookingClose || 1);
      }
    } catch (error) {
      console.error("Error fetching booking settings:", error);
      // Use default value if error
      setHoursBeforeBookingClose(1);
    }
  };

  const fetchClassSchedule = async () => {
    try {
      const scheduleSnapshot = await getDocs(collection(db, "classSchedule"));
      const scheduleList = scheduleSnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setClassSchedule(scheduleList);
    } catch (error) {
      console.error("Error fetching class schedule: ", error);
      setErrorMessage("Failed to fetch class schedule. Please try again later.");
    }
  };

  const handleClassSelect = async (classItem, date) => {
    try {
      const bookingDoc = await getDoc(doc(db, "settings", "booking"));
      const daysAllowedPrior = bookingDoc.exists() ? bookingDoc.data().daysAllowedPrior : 2;
      const hours = bookingDoc.exists() ? bookingDoc.data().hoursBeforeBookingClose : 1;

      const classDateTime = moment(`${date} ${classItem.startTime}`, 'YYYY-MM-DD HH:mm');
      const now = moment();
      const daysDifference = classDateTime.diff(now, 'days');

      if (classDateTime.isBefore(now)) {
        alert("You cannot book classes in the past.");
        setIsLoading(false);
        return;
      }

      // Prevent booking within the configured hours before class start
      const minutesDifference = classDateTime.diff(now, 'minutes');
      const minutesThreshold = hours * 60;
      if (minutesDifference <= minutesThreshold) {
        alert(`Bookings close ${hours} hour${hours > 1 ? 's' : ''} before the class start. Please book earlier.`);
        return;
      }

      if (daysDifference >= daysAllowedPrior) {
        alert(`Bookings can only be made up to ${daysAllowedPrior} days in advance.`);
        setIsLoading(false);
        return;
      }

      setSelectedClass({ ...classItem, date });
      setErrorMessage('');
      setCurrentStep('login');
    } catch (error) {
      console.error("Error fetching booking settings:", error);
      alert("An error occurred while processing your request. Please try again.");
    }
  };


  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setErrorMessage('');
    if (!email || !password) {
      alert("Please enter both email and password.");
      setIsLoading(false);
      return;
    }
    
    try {
      
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      

      const membersRef = doc(db, "members", user.uid);
      const memberDoc = await getDoc(membersRef);
      
      if (!memberDoc.exists()) {
        setErrorMessage("No membership found for this email. Please sign up for a membership.");
        setIsLoading(false);
        alert("No membership found for this email. Please sign up for a membership.");
        return;
      }

      const memberData = memberDoc.data();
     
      if (!memberData.member) {
        setErrorMessage("You are not a registered member. Please sign up for a membership.");
        setIsLoading(false);
        alert("You are not a registered member. Please sign up for a membership.");
        return;
      }
     
      const eligibilityCheck = await checkMembershipEligibility(memberData, selectedClass.date);
    

      if (eligibilityCheck !== "eligible") {
        
        showModal(eligibilityCheck);
        setIsLoading(false);
        return;
      }
      
      await handleBookClass(user.uid);
    } catch (error) {
      console.error("Error in handleLogin:", error);
      setErrorMessage(`Login error: ${error.message}`);
      console.error("Error logging in: ", error);
      alert("Invalid email or password. Please try again.");
    }
  };

  const checkMembershipEligibility = async (memberData, classDate) => {
    const { bookedClasses = [], classesRemaining = 0, membershipEndDate, status } = memberData;
    const currentDate = moment();
    const classDateMoment = moment(classDate);

    // Check if membership has expired
    
    if (membershipEndDate && moment(membershipEndDate.toDate()).isBefore(currentDate)) {
      setIsLoading(false);
      return "Your membership has expired. Please renew to book classes.";
    }

    try {
      const membershipDetailsRef = collection(db, "membershipDetails");
      const q = query(membershipDetailsRef, where("name", "==", status));
      
      const querySnapshot = await getDocs(q);
      
      if (querySnapshot.empty) {
        setIsLoading(false);
        return null;
       
      }

      const membershipDetails = querySnapshot.docs[0].data();
    
      if (!membershipDetails) {
        setIsLoading(false);
        return "Invalid membership status.";
       
      }
      if (membershipDetails.isUnlimitedDuration) {
        const bookedClassesThisWeek = bookedClasses.filter(booking =>
          moment(booking.date).isSame(classDateMoment, 'week')
        ).length;

        if (!isNaN(parseInt(membershipDetails.classesPerWeek))) {
          if (bookedClassesThisWeek >= membershipDetails.classesPerWeek) {
            return `You've reached your weekly limit of ${membershipDetails.classesPerWeek} classes.`;
          }
        }
        return "eligible";
      }

      if (classesRemaining <= 0) {
        return "You've used all classes on your memebership.";
      }

      return "eligible";
    } catch (error) {
      console.error("Error checking membership eligibility:", error);
      return "An error occurred while checking eligibility. Please try again.";
    }
  };
  const handleBookClass = async (memberId) => {
    setIsLoading(true);
    setErrorMessage('');
    
    if (!selectedClass || !email) {
      alert("Please select a class and enter your email.");
      return;
    }

    try {
      
      const classRef = doc(db, "classSchedule", selectedClass.id);
      const classDoc = await getDoc(classRef);
      const classData = classDoc.data();

      if (!classData) {
        alert("Invalid class data. Please try again.");
        setIsLoading(false);
        return;
      }

      // Check if the member has already booked this class on the selected date
      const existingBooking = classData.bookings?.[selectedClass.date]?.memberIds?.includes(memberId);

      if (existingBooking) {
        alert("You have already booked this class for the selected date.");
        setIsLoading(false);
        return;
      }

      // Initialize the `bookings` object if it doesn't exist
      const bookingsForDate = classData.bookings?.[selectedClass.date] || {
        bookedSpots: 0,
        availableSpots: classData.availableSpots // Default to class-level available spots
      };

      if (bookingsForDate.bookedSpots >= bookingsForDate.availableSpots) {
        alert("This class is full on the selected date.");
        setIsLoading(false);
        return;
      }

      // Increment the spots for the selected date
      await updateDoc(classRef, {
        [`bookings.${selectedClass.date}.bookedSpots`]: bookingsForDate.bookedSpots + 1,
        [`bookings.${selectedClass.date}.memberIds`]: arrayUnion(memberId)
      });

      // Update member data
      await updateDoc(doc(db, "members", memberId), {
        bookedClasses: arrayUnion({
          classId: selectedClass.id,
          date: selectedClass.date,
          className: selectedClass.className,
          startTime: selectedClass.startTime,
          endTime: selectedClass.endTime
        })
      });

      await addDoc(collection(db, "bookings"), {
        userId: memberId,
        classId: selectedClass.id,
        className: selectedClass.className,
        date: selectedClass.date,
        startTime: selectedClass.startTime,
        endTime: selectedClass.endTime,
        email: email
      });

      setErrorMessage('');
      alert(`Class "${selectedClass.className}" booked successfully for ${selectedClass.date} at ${selectedClass.startTime}.`);
      setSelectedClass(null);
      setEmail('');
      setCurrentStep('calendar');
      fetchClassSchedule();
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
      console.error("Error booking class: ", error);
      setErrorMessage(`Error booking class: ${error.message}`);
    }
  };

  const renderBreadcrumb = () => (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-5 py-3 mb-4">
      <div className="flex flex-wrap items-center gap-1.5 text-sm">
        <span
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-colors cursor-pointer ${currentStep === '' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-400 hover:text-gray-600'}`}
          onClick={() => setCurrentStep('')}
        >
          <FaHome size={11} />
          <span>Home</span>
        </span>
        <FaChevronRight className="text-gray-200" size={10} />
        <span
          className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg transition-colors cursor-pointer ${currentStep === 'calendar' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-400 hover:text-gray-600'}`}
          onClick={() => setCurrentStep('calendar')}
        >
          <FaCalendarAlt size={11} />
          <span>Select Class</span>
        </span>
        <FaChevronRight className="text-gray-200" size={10} />
        <span className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${currentStep === 'login' ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'text-gray-300'}`}>
          <FaSignInAlt size={11} />
          <span>Login</span>
        </span>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      {isLoading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        {/* Page header */}
        <div className="flex items-center gap-3 mb-5">
          <div className="bg-slate-800 p-2.5 rounded-xl">
            <FaCalendarAlt className="text-white text-base" />
          </div>
          <div>
            <h2 className="text-base font-bold text-gray-800 tracking-wider uppercase">Class Booking</h2>
            <p className="text-xs text-gray-400 mt-0.5">Browse and reserve your spot</p>
          </div>
        </div>

        {renderBreadcrumb()}

        {errorMessage && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-4 rounded-xl text-sm font-medium">
            {errorMessage}
          </div>
        )}

        {currentStep === '' && navigate("/")}

        {currentStep === 'calendar' && (
          <Calendar classSchedule={classSchedule} onClassSelect={handleClassSelect} />
        )}

        {currentStep === 'login' && (
          <div className="max-w-lg mx-auto">
            {/* Class summary card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden mb-4">
              <div className="bg-slate-800 px-5 py-4 flex items-center gap-2">
                <FaCheckCircle className="text-emerald-400 shrink-0" size={14} />
                <h3 className="text-sm font-bold text-white tracking-wide uppercase">Selected Class</h3>
              </div>
              <div className="p-5">
                <h4 className="text-lg font-bold text-gray-800 mb-4">{selectedClass.className}</h4>
                <div className="space-y-2.5">
                  {selectedClass.coaches && selectedClass.coaches.length > 0 && (
                    <div className="flex items-center gap-3 text-sm text-gray-600">
                      <FaUsers className="text-slate-400 shrink-0" size={13} />
                      <span><span className="font-semibold text-gray-700">Instructors:</span> {selectedClass.coaches.join(', ')}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <FaCalendarAlt className="text-slate-400 shrink-0" size={13} />
                    <span><span className="font-semibold text-gray-700">Date:</span> {moment(selectedClass.date).format('MMMM DD, YYYY')}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-gray-600">
                    <FaClock className="text-slate-400 shrink-0" size={13} />
                    <span><span className="font-semibold text-gray-700">Time:</span> {selectedClass.startTime} – {selectedClass.endTime}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Login form card */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
              <h4 className="text-sm font-bold text-gray-800 tracking-wider uppercase mb-5 flex items-center gap-2">
                <FaSignInAlt className="text-slate-400" size={13} />
                Login to Confirm Booking
              </h4>

              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Email Address
                  </label>
                  <div className="relative">
                    <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" size={13} />
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                      placeholder="Enter your email"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="password" className="block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5">
                    Password
                  </label>
                  <div className="relative">
                    <FaLock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-300 pointer-events-none" size={13} />
                    <input
                      type="password"
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-400 focus:border-transparent transition"
                      placeholder="Enter your password"
                      required
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-indigo-600 hover:bg-indigo-700 active:scale-[0.99] text-white py-3 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 flex items-center justify-center gap-2 mt-2"
                >
                  <FaCheckCircle size={13} />
                  Confirm Booking
                </button>
              </form>
            </div>

            <button
              onClick={() => setCurrentStep('calendar')}
              className="mt-3 w-full text-center text-xs text-gray-400 hover:text-gray-600 transition-colors py-2"
            >
              ← Back to class selection
            </button>
          </div>
        )}

        <Modal isOpen={modalOpen} onClose={() => setModalOpen(false)}>
          <p>{modalMessage}</p>
        </Modal>
      </div>
    </div>
  );
};

export default ClassBooking;
